import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Modal from 'react-bootstrap/Modal'
import Datetime from 'react-datetime'
import moment from 'moment'
import Form from 'react-bootstrap/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'

const DATE_FORMAT = 'MM/DD/YYYY'

// Returns the nth occurrence of a weekday in a given month/year.
// weekday: 0=Sunday … 6=Saturday
const nthWeekday = (year, month, weekday, n) => {
  const first = moment({ year, month, day: 1 })
  let diff = (weekday - first.day() + 7) % 7
  const d = first.clone().add(diff, 'days').add((n - 1) * 7, 'days')
  return d
}

// Returns the last occurrence of a weekday in a given month/year.
const lastWeekday = (year, month, weekday) => {
  const last = moment({ year, month }).endOf('month')
  let diff = (last.day() - weekday + 7) % 7
  return last.clone().subtract(diff, 'days')
}

const FEDERAL_HOLIDAYS = [
  { name: "New Year's Day", date: (y) => moment({ year: y, month: 0, day: 1 }) },
  { name: 'Martin Luther King Jr. Day', date: (y) => nthWeekday(y, 0, 1, 3) },
  { name: "Presidents' Day", date: (y) => nthWeekday(y, 1, 1, 3) },
  { name: 'Memorial Day', date: (y) => lastWeekday(y, 4, 1) },
  { name: 'Juneteenth', date: (y) => moment({ year: y, month: 5, day: 19 }) },
  { name: 'Independence Day', date: (y) => moment({ year: y, month: 6, day: 4 }) },
  { name: 'Labor Day', date: (y) => nthWeekday(y, 8, 1, 1) },
  { name: 'Columbus Day', date: (y) => nthWeekday(y, 9, 1, 2) },
  { name: 'Veterans Day', date: (y) => moment({ year: y, month: 10, day: 11 }) },
  { name: 'Thanksgiving Day', date: (y) => nthWeekday(y, 10, 4, 4) },
  { name: 'Christmas Day', date: (y) => moment({ year: y, month: 11, day: 25 }) }
]

const getFederalHolidays = (year) =>
  FEDERAL_HOLIDAYS.map((h) => ({ name: h.name, date: h.date(year).format(DATE_FORMAT) }))

// Reverse lookup: given a date string, return the holiday name if it matches any federal holiday.
const getHolidayName = (dateStr) => {
  const m = moment(dateStr, DATE_FORMAT, true)
  if (!m.isValid()) return null
  const year = m.year()
  const holidays = getFederalHolidays(year)
  const match = holidays.find((h) => h.date === dateStr)
  return match ? match.name : null
}

const HolidayDatePicker = ({ label, selectedDates, defaultDates, onChange, disabled }) => {
  const [show, setShow] = useState(false)
  const [localDates, setLocalDates] = useState([])
  const [pickerValue, setPickerValue] = useState('')
  const [holidayYear, setHolidayYear] = useState(moment().year())

  const currentYear = moment().year()
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i)

  const handleOpen = () => {
    setLocalDates([...(selectedDates || [])])
    setPickerValue('')
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
  }

  const handleSave = () => {
    onChange(localDates)
    setShow(false)
  }

  const handleAddDate = () => {
    const m = moment(pickerValue, DATE_FORMAT, true)
    if (!m.isValid()) return

    const formatted = m.format(DATE_FORMAT)
    if (localDates.includes(formatted)) return

    setLocalDates((prev) => [...prev, formatted])
    setPickerValue('')
  }

  const handleAddFederalHoliday = (dateStr) => {
    if (localDates.includes(dateStr)) return
    setLocalDates((prev) => [...prev, dateStr])
  }

  const handleAddAllFederalHolidays = () => {
    const newDates = federalHolidays
      .map((h) => h.date)
      .filter((d) => !localDates.includes(d))
    if (newDates.length === 0) return
    setLocalDates((prev) => [...prev, ...newDates])
  }

  const handlePickerChange = (value) => {
    if (moment.isMoment(value)) {
      setPickerValue(value.format(DATE_FORMAT))
    } else {
      setPickerValue(value)
    }
  }

  const handleRemove = (date) => {
    setLocalDates((prev) => prev.filter((d) => d !== date))
  }

  const handleClearAll = () => {
    setLocalDates([])
  }

  const handleRevertToDefault = () => {
    setLocalDates([...(defaultDates || [])])
  }

  const groupByYear = (dates) => {
    const groups = {}
    dates.forEach((d) => {
      const m = moment(d, DATE_FORMAT, true)
      if (!m.isValid()) return
      const year = m.year()
      if (!groups[year]) groups[year] = []
      groups[year].push(d)
    })

    Object.keys(groups).forEach((year) => {
      groups[year].sort((a, b) => moment(a, DATE_FORMAT).diff(moment(b, DATE_FORMAT)))
    })

    return Object.keys(groups)
      .sort((a, b) => b - a)
      .map((year) => ({ year, dates: groups[year] }))
  }

  const count = (selectedDates || []).length
  const federalHolidays = getFederalHolidays(holidayYear)

  return (
    <>
      <Button
        variant="light"
        size="sm"
        onClick={handleOpen}
        disabled={disabled}
      >
        {count} holiday{count !== 1 ? 's' : ''} configured
      </Button>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Datetime
              dateFormat={DATE_FORMAT}
              timeFormat={false}
              input={true}
              value={pickerValue}
              onChange={handlePickerChange}
              inputProps={{
                className: 'form-control form-control-sm',
                placeholder: 'Select a date...'
              }}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddDate}
              className="text-nowrap"
            >
              Add Date
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={handleRevertToDefault}
              className="text-nowrap"
            >
              Revert to Default
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={handleClearAll}
              className="text-nowrap"
            >
              Clear All
            </Button>
          </div>
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="small fw-bold text-muted">Federal Holidays</span>
              <Form.Select
                size="sm"
                value={holidayYear}
                onChange={(e) => setHolidayYear(Number(e.target.value))}
                style={{ width: 'auto' }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddAllFederalHolidays}
                disabled={federalHolidays.every((h) => localDates.includes(h.date))}
                style={{ fontSize: '0.8em' }}
              >
                Select All
              </Button>
            </div>
            <div className="d-flex flex-wrap gap-1">
              {federalHolidays.map((h) => {
                const alreadyAdded = localDates.includes(h.date)
                return (
                  <Button
                    key={h.name}
                    variant={alreadyAdded ? 'outline-secondary' : 'outline-primary'}
                    size="sm"
                    disabled={alreadyAdded}
                    onClick={() => handleAddFederalHoliday(h.date)}
                    style={{ fontSize: '0.8em' }}
                  >
                    {h.name}
                  </Button>
                )
              })}
            </div>
          </div>
          <div className="text-muted small mb-2">
            {localDates.length} holiday{localDates.length !== 1 ? 's' : ''}
          </div>
          {groupByYear(localDates).map(({ year, dates }) => (
            <div key={year} className="mb-2">
              <div className="small fw-bold text-muted mb-1">{year}</div>
              <div className="d-flex flex-wrap gap-1">
                {dates.map((date) => {
                  const holidayName = getHolidayName(date)
                  return (
                    <Badge
                      key={date}
                      bg="light"
                      text="primary"
                      className="d-inline-flex align-items-center border"
                      style={{ cursor: 'pointer', fontSize: '0.85em' }}
                      onClick={() => handleRemove(date)}
                    >
                      {holidayName ? `${holidayName} (${date})` : date}
                      <FontAwesomeIcon
                        icon={faTimesCircle}
                        className="ms-1 text-muted"
                        style={{ fontSize: '0.9em' }}
                      />
                    </Badge>
                  )
                })}
              </div>
            </div>
          ))}
          {localDates.length === 0 && (
            <span className="text-muted small">No holidays added</span>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Holidays
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

HolidayDatePicker.propTypes = {
  label: PropTypes.string.isRequired,
  selectedDates: PropTypes.arrayOf(PropTypes.string),
  defaultDates: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default HolidayDatePicker
